import { FormSection } from '../ui/FormSection';
import type { ProfessionalScoreEntry } from './types';

export interface WineProfessionalScoresFieldsProps {
  professionalScores: ProfessionalScoreEntry[];
  onProfessionalScoresChange: (scores: ProfessionalScoreEntry[]) => void;
  isSubmitting: boolean;
}

export const WineProfessionalScoresFields = ({
  professionalScores,
  onProfessionalScoresChange,
  isSubmitting,
}: WineProfessionalScoresFieldsProps) => {
  const addProfessionalScore = () => {
    onProfessionalScoresChange([...professionalScores, { critic: '', score: '' }]);
  };

  const removeProfessionalScore = (index: number) => {
    onProfessionalScoresChange(professionalScores.filter((_, i) => i !== index));
  };

  const updateProfessionalScore = (index: number, field: 'critic' | 'score', value: string) => {
    const updated = [...professionalScores];
    updated[index] = { ...updated[index], [field]: value };
    onProfessionalScoresChange(updated);
  };

  return (
    <FormSection
      title="Professional Scores"
      actions={
        <button
          type="button"
          onClick={addProfessionalScore}
          disabled={isSubmitting}
          className="px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add Score
        </button>
      }
    >
      {professionalScores.length === 0 ? (
        <p className="text-sm text-gray-500">No professional scores added yet.</p>
      ) : (
        <div className="space-y-3">
          {professionalScores.map((score, index) => (
            <div key={index} className="flex gap-3 items-start">
              <div className="flex-1">
                <input
                  type="text"
                  value={score.critic}
                  onChange={(e) => updateProfessionalScore(index, 'critic', e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Critic name (e.g., Wine Spectator)"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
                    isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                />
              </div>
              <div className="w-24">
                <input
                  type="text"
                  inputMode="numeric"
                  value={score.score}
                  onChange={(e) => updateProfessionalScore(index, 'score', e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Score"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
                    isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={() => removeProfessionalScore(index)}
                disabled={isSubmitting}
                className="px-3 py-2 text-red-600 hover:text-red-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Remove score"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </FormSection>
  );
};
